import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef, Table } from '@tanstack/react-table';
import { toast } from 'sonner';
import { ArrowLeft, ListPlus, Loader2, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { downloadCsv, stampedFileName } from '@/components/data-table/csv-export';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import { postApiCall } from '@/service/httpCall';
import { API_CONTACT_CUSTOM_FIELD_TEMPLATES_DELETE, API_CONTACT_CUSTOM_FIELD_TEMPLATES_RENAME } from '@/service/url';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { MODULE_ACL, ROUTES } from '@/utilities/constant';

import {
  MAX_CUSTOM_FIELDS,
  MAX_NAME_LEN,
  TEMPLATE_NAME_REGEX,
  TYPE_LABELS,
  type CustomField,
  type CustomFieldTemplate,
} from './customFields';
import { TemplateFieldsCell } from './TemplateFieldsCell';
import { useCustomFieldTemplates } from './useCustomFieldTemplates';

const formatDate = (s?: string) => {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const inputCls = 'input-embossed h-9 w-full rounded-md px-2.5 text-sm disabled:opacity-50';

/** One field for the export, e.g. "Age (Numbers; Required; Default: 18; Min: 18; Max: 60)". */
function fieldExportText(f: CustomField): string {
  const show = (v: unknown) => (Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v));
  const parts = [
    TYPE_LABELS[f.field_type] || f.field_type,
    f.is_required ? 'Required' : 'Optional',
    f.field_default !== null && f.field_default !== undefined && f.field_default !== '' ? `Default: ${show(f.field_default)}` : '',
    f.field_min !== null && f.field_min !== undefined ? `Min: ${f.field_min}` : '',
    f.field_max !== null && f.field_max !== undefined ? `Max: ${f.field_max}` : '',
    f.options?.length ? `Options: ${f.options.join(', ')}` : '',
  ].filter(Boolean);
  return `${f.field_name} (${parts.join('; ')})`;
}

export default function CustomFieldTemplatesPage() {
  const navigate = useNavigate();
  const { canRead, canAdd, canEdit, canDelete } = useModuleAcl(MODULE_ACL.CONTACTS);
  const { enabled, templates, loading, reload } = useCustomFieldTemplates();
  // One template (row button) or several (selection bar); clear empties the selection after a delete.
  const [pendingDelete, setPendingDelete] = useState<{ items: CustomFieldTemplate[]; skipped: number; clear?: () => void } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [renaming, setRenaming] = useState<CustomFieldTemplate | null>(null);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setPendingDelete(null);
      setRenaming(null);
    }
  }, [enabled]);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const { items, clear } = pendingDelete;
    postApiCall(API_CONTACT_CUSTOM_FIELD_TEMPLATES_DELETE, { ids: items.map((t) => t.id) })
      .then(() => {
        toast.success(items.length === 1 ? 'Template deleted' : `${items.length} templates deleted`);
        clear?.();
        setPendingDelete(null);
        void reload();
      })
      .catch((err: unknown) => toast.error(friendlyApiError(err) || 'Failed to delete templates'))
      .finally(() => setDeleting(false));
  };

  const openRename = (t: CustomFieldTemplate) => {
    setRenaming(t);
    setNewName(t.name);
  };

  const saveName = () => {
    if (!renaming) return;
    const name = newName.trim();
    if (!name) {
      toast.error('Template name is required');
      return;
    }
    if (name === renaming.name) {
      setRenaming(null);
      return;
    }
    setSavingName(true);
    postApiCall(API_CONTACT_CUSTOM_FIELD_TEMPLATES_RENAME, { id: renaming.id, name })
      .then(() => {
        toast.success('Template renamed');
        setRenaming(null);
        void reload();
      })
      .catch((err: unknown) => toast.error(friendlyApiError(err) || 'Failed to rename template'))
      .finally(() => setSavingName(false));
  };

  // The rows as filtered / sorted on screen, with every field's settings spelled out.
  const exportTemplates = (table: Table<CustomFieldTemplate>) => {
    const rows = table.getSortedRowModel().rows.map((r) => r.original);
    downloadCsv(
      [
        // A plain count: Excel reads "1 / 10" in a CSV as the date 1-Oct.
        ['Template', 'Fields', `Field Count (max ${MAX_CUSTOM_FIELDS})`, 'Used In (Contacts)', 'Created', 'Updated'],
        ...rows.map((t) => [
          t.name,
          t.fields.map(fieldExportText).join(' | '),
          t.fields.length,
          t.contact_count || 0,
          formatDate(t.created_at),
          formatDate(t.updated_at),
        ]),
      ],
      stampedFileName('custom-field-templates'),
    );
  };

  const columns = useMemo<ColumnDef<CustomFieldTemplate>[]>(() => {
    const cols: ColumnDef<CustomFieldTemplate>[] = [
      {
        accessorKey: 'name',
        header: 'Template',
        size: 220,
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5">
            <span className="font-medium text-foreground">{row.original.name}</span>
            {canEdit && (
              <button
                type="button"
                title="Rename template"
                aria-label={`Rename ${row.original.name}`}
                onClick={() => openRename(row.original)}
                className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition hover:bg-primary/10 hover:text-primary"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
          </span>
        ),
      },
      {
        id: 'fields',
        header: 'Fields',
        enableSorting: false,
        cell: ({ row }) => <TemplateFieldsCell template={row.original} />,
      },
      {
        id: 'count',
        header: 'Count',
        size: 90,
        accessorFn: (t) => t.fields.length,
        cell: ({ row }) => (
          <span className="tabular-nums text-sm">
            {row.original.fields.length} / {MAX_CUSTOM_FIELDS}
          </span>
        ),
      },
      {
        id: 'contact_count',
        header: 'Used in',
        size: 130,
        accessorFn: (t) => t.contact_count || 0,
        cell: ({ row }) => {
          const n = row.original.contact_count || 0;
          return (
            <span className={n ? 'text-sm font-medium text-foreground tabular-nums' : 'text-sm text-muted-foreground'}>
              {n.toLocaleString()} contact{n === 1 ? '' : 's'}
            </span>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        size: 180,
        cell: ({ row }) => <span className="text-xs text-muted-foreground">{formatDate(row.original.created_at)}</span>,
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        size: 180,
        cell: ({ row }) => <span className="text-xs text-muted-foreground">{formatDate(row.original.updated_at)}</span>,
      },
    ];
    if (canDelete) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        size: 110,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          // A template used by any contact cannot be deleted.
          const used = (row.original.contact_count || 0) > 0;
          return (
            <button
              type="button"
              title={used ? `Used in ${row.original.contact_count} contact(s); cannot be deleted` : 'Delete template'}
              disabled={used}
              onClick={() => setPendingDelete({ items: [row.original], skipped: 0 })}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-rose-100 dark:bg-rose-950/40 dark:text-rose-300"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          );
        },
      });
    }
    return cols;
  }, [canEdit, canDelete]);

  if (!enabled || !canRead) {
    return (
      <FillPage>
        <PageHeader title="Contact Custom Fields" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          Custom fields are not enabled in your assigned ACL (Contacts → Options → Custom fields).
        </div>
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Contact Custom Fields"
        description={`Templates of extra contact fields. Each template can have up to ${MAX_CUSTOM_FIELDS} fields (20 in total with the 10 contact fields).`}
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={() => void reload()}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={'h-4 w-4 ' + (loading ? 'animate-spin' : '')} />
            </Button>
            <Button variant="outline" onClick={() => navigate(`/${ROUTES.CONTACTGROUP.ALL_CONTACTS}`)}>
              <ArrowLeft className="h-4 w-4" />
              All Contacts
            </Button>
            {canAdd && (
              <Button onClick={() => navigate(`/${ROUTES.CONTACTGROUP.CREATE_CUSTOM_FIELD_TEMPLATE}`)}>
                <Plus className="h-4 w-4" />
                Create Template
              </Button>
            )}
          </>
        }
      />

      <DataTable
        // v2: saved table state from v1 had no sort, which would hide the newest-first default.
        tableId="contacts:custom-field-templates:v2"
        initialSorting={[{ id: 'updated_at', desc: true }]}
        columns={columns}
        data={templates}
        isLoading={loading}
        searchPlaceholder="Search templates…"
        emptyTitle="No custom field templates yet"
        emptyDescription="Create a template, then pick it while creating, editing or uploading contacts."
        getRowId={(row) => String(row.id)}
        enableRowSelection={canDelete}
        renderSelectionActions={(ids, clear) => {
          const selected = templates.filter((t) => ids.includes(String(t.id)));
          // Templates used by contacts cannot be deleted; they are left out.
          const deletable = selected.filter((t) => !(t.contact_count || 0));
          return (
            <Button
              size="sm"
              variant="destructive"
              disabled={deleting || !deletable.length}
              title={deletable.length ? undefined : 'Templates used in contacts cannot be deleted'}
              onClick={() => setPendingDelete({ items: deletable, skipped: selected.length - deletable.length, clear })}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete ({deletable.length})
            </Button>
          );
        }}
        initialColumnPinning={canDelete ? { right: ['actions'] } : undefined}
        pageSize={10}
        onExport={exportTemplates}
      />

      <Dialog
        open={!!renaming}
        onOpenChange={(o) => {
          if (!o && !savingName) setRenaming(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rename template</DialogTitle>
            <DialogDescription>
              Only the name changes. Its fields and the values saved on contacts stay as they are.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label htmlFor="template-new-name" className="text-xs font-semibold text-foreground">
              Template Name <span className="text-destructive">*</span>
            </label>
            <input
              id="template-new-name"
              autoFocus
              value={newName}
              maxLength={MAX_NAME_LEN}
              disabled={savingName}
              onChange={(e) => {
                if (TEMPLATE_NAME_REGEX.test(e.target.value)) setNewName(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveName();
              }}
              className={inputCls}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)} disabled={savingName}>
              Cancel
            </Button>
            <Button onClick={saveName} disabled={savingName}>
              {savingName && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
        title="Are you sure you want to delete?"
        description={
          <span className="flex items-start gap-2">
            <ListPlus className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>
              {pendingDelete?.items.length === 1 ? (
                <>
                  Template <b>{pendingDelete.items[0]?.name}</b> will be removed from the dropdowns.
                </>
              ) : (
                <>
                  <b>{pendingDelete?.items.length} templates</b> will be removed from the dropdowns.
                </>
              )}{' '}
              Values already saved on contacts are kept.
              {!!pendingDelete?.skipped && (
                <span className="mt-1 block">
                  {pendingDelete.skipped} selected template{pendingDelete.skipped === 1 ? ' is' : 's are'} used in contacts and
                  will not be deleted.
                </span>
              )}
            </span>
          </span>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
}
